import Footer from '@/components/footer';
import Title from '@/components/title';
import { useProducts } from '@/hooks/useProducts';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useParams } from '@tanstack/react-router';
import { ArrowLeft, CircleCheck } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { Challenge } from '../types/index';
import { getMission } from '@/lib/api';

const cleanHtmlContent = (html: string): string => {
  return html.replace(/style="color:\s*rgb\(0,\s*0,\s*0\)"/g, '');
};

export const Route = createFileRoute('/challenge/$id')({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = useParams({ from: '/challenge/$id' });
  const { products, groupedProducts, isSpecialUser } = useProducts();
  const [mission, setMission] = useState<Challenge>();

  useEffect(() => {
    // Scroll to top on page load to prevent unexpected scroll behavior
    window.scrollTo(0, 0);

    const fetchMission = async () => {
      try {
        const response = await getMission(Number(id));
        setMission(response);
      } catch (error) {
        console.error('Error fetching mission details:', error);
      }
    };

    fetchMission();
  }, [id]);

  const mergedConditions = useMemo(
    () => [
      ...(mission?.mission_conditions &&
      Array.isArray(mission.mission_conditions)
        ? mission.mission_conditions
        : []),
      ...(mission?.rewardRules?.config?.mission_conditions &&
      Array.isArray(mission.rewardRules.config.mission_conditions)
        ? mission.rewardRules.config.mission_conditions
        : []),
    ],
    [mission]
  );

  return (
    <>
      <section
        className="h-[35vh] flex flex-row bg-cover bg-center text-white relative"
        style={{
          backgroundImage: mission?.picture
            ? `url(${mission.picture})`
            : undefined,
        }}
      />
      <div className="px-4 md:px-0 pt-[74px] pb-24">
        <div className="container mx-auto flex flex-col gap-4">
          <div className="flex flex-row gap-2 items-center">
            <Link to="/challenges" className="flex flex-row gap-2 items-center">
              <ArrowLeft size={24} />
              <p className="text-xl underline">
                Küldetések / <span className="font-bold">{mission?.name}</span>
              </p>
            </Link>
          </div>
          <Title text={mission?.name || ''} className="mb-0" />
          <p className="text-2xl font-bold mb-[3.625rem]">{mission?.summary}</p>
          <div
            className="prose prose-lg !max-w-none [&_ul]:!list-disc [&_ul]:!ml-3 [&_ul]:!mb-4 [&_ol]:!list-decimal [&_ol]:!ml-3 [&_ol]:!mb-4 [&_ul_li]:!ml-3 [&_ol_li]:!ml-3 [&_ul_li]:!pl-2 [&_ol_li]:!pl-2 [&_p]:!my-4 [&_p]:first:!mt-0 [&_p]:last:!mb-0 [&_p]:!text-base [&_p]:!leading-relaxed [&_h1]:!text-heineken-green [&_h1]:!font-heineken [&_h1]:!text-4xl !mx-auto"
            dangerouslySetInnerHTML={{
              __html: mission?.description
                ? cleanHtmlContent(mission.description)
                : 'Részletes leírás nem érhető el ehhez a küldetéshez.',
            }}
          />
          {mission?.rewardRuleType.code === 'visibility' ||
          mission?.rewardRuleType.code === 'training' ? (
            <div>
              {mission?.trainer_name && (
                <p className="text-lg text-gray-600 mb-4">
                  <span className="font-semibold">Trainer:</span>{' '}
                  {mission.trainer_name}
                </p>
              )}
              <div className="flex flex-col border border-gray-200 rounded-[20px] shadow-sm">
                <div className="flex flex-col px-6 pt-8 pb-4">
                  <p className="text-2xl font-bold">Teljesítési feltételek</p>
                  <p className="text-xl">
                    A küldetés teljesítéséhez kövesse az alábbi feltételeket:
                  </p>
                </div>
                <div className="flex flex-col p-6 gap-8">
                  {mergedConditions.map((condition: any) => (
                    <div
                      key={condition.uuid}
                      className="flex flex-row gap-2 items-center"
                    >
                      <CircleCheck className="text-heineken-green" size={24} />
                      <p className="text-xl font-bold">
                        {condition?.condition}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : mission?.rewardRuleType.code === 'innovation' ? (
            <div>
              <div className="flex flex-col border border-gray-200 rounded-[20px] shadow-sm">
                <div className="flex flex-col px-6 pt-8 pb-4">
                  <p className="text-2xl font-bold">Teljesítési feltételek</p>
                  <p className="text-xl">
                    A küldetés teljesítéséhez a következő termékekből kell
                    vásárolni:
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-heineken-green text-white">
                        <th className="px-6 py-4 text-left font-bold">SKU</th>
                        <th className="px-6 py-4 text-left font-bold">
                          Termék neve
                        </th>
                        <th className="px-6 py-4 text-left font-bold">Márka</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mission?.rewardRules?.config?.products?.map(
                        (product, index) => (
                          <tr
                            key={product.id}
                            className={
                              index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                            }
                          >
                            <td className="px-6 py-4 border-b border-gray-200">
                              <p className="text-gray-600 text-sm">
                                {product.sku}
                              </p>
                            </td>
                            <td className="px-6 py-4 border-b border-gray-200">
                              <p>{product.name}</p>
                            </td>
                            <td className="px-6 py-4 border-b border-gray-200">
                              <p className="font-semibold">{product.brand}</p>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : mission?.rewardRuleType.code === 'flash' ? (
            <div>
              <div className="flex flex-col border border-gray-200 rounded-[20px] shadow-sm">
                <div className="flex flex-col px-6 pt-8 pb-4">
                  <p className="text-2xl font-bold">Teljesítési feltételek</p>
                  <p className="text-xl">
                    A küldetés teljesítéséhez a következő termékekből kell
                    vásárolni:
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-heineken-green text-white">
                        <th className="px-6 py-4 text-left font-bold">SKU</th>
                        <th className="px-6 py-4 text-left font-bold">
                          Kredit szorzó
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {mission?.rewardRules?.config?.flash_deal_items?.map(
                        (item, index) => (
                          <tr
                            key={item.skuId}
                            className={
                              index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                            }
                          >
                            <td className="px-6 py-4 border-b border-gray-200">
                              <p className="text-gray-600 text-sm">
                                {item.skuId}
                              </p>
                            </td>
                            <td className="px-6 py-4 border-b border-gray-200">
                              <p className="font-semibold">
                                {item.creditMultiplier}
                              </p>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <Footer
        productList={
          isSpecialUser ? groupedProducts.flatMap((g) => g.products) : products
        }
      />
    </>
  );
}
